// src/App.js - Template Marketplace Routes
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  useLocation,
  Route,
  Navigate,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { SupabaseAuthProvider } from './auth/SupabaseAuthProvider.js';
import { PageTransition } from './shared/components/FramerAnimations';
import PageWrapper from './marketing/components/PageWrapper';
import ScrollToTop from './shared/components/ScrollToTop';

// Auth components and routes
import AdminProtectedRoute from './auth/AdminProtectedRoute';
import BusinessLogin from './auth/BusinessLogin';
import BusinessSignup from './auth/BusinessSignup';
import SignOut from './auth/SignOut';
import AccountCreatedSuccess from './auth/AccountCreatedSuccess';
import UnauthorizedPage from './auth/UnauthorizedPage';

// Marketing pages
import HomePage from './marketing/HomePage';
import PricingPage from './marketing/PricingPage';
import ContactPage from './marketing/ContactPage';
import FeaturesPage from './marketing/FeaturesPage.js';
import ResourcesPage from './marketing/ResourcesPage.js';
import ComingSoonPage from './marketing/ComingSoonPage';

// Marketplace pages
import TemplateBrowse from './marketplace/TemplateBrowse';
import TemplateDetail from './marketplace/TemplateDetail';

// Admin application
import AdminApp from './app/AdminApp';
import SuperAdminApp from './admin/SuperAdminApp';

import ForgotPassword from './auth/ForgotPassword.js';

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <ScrollToTop behavior="smooth" delay={200} />

      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          {/* Marketing Pages */}
          <Route
            path="/"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={true}
              >
                <PageTransition>
                  <HomePage />
                </PageTransition>
              </PageWrapper>
            }
          />
          <Route
            path="/pricing"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={true}
              >
                <PageTransition>
                  <PricingPage />
                </PageTransition>
              </PageWrapper>
            }
          />
          <Route
            path="/contact"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={false}
              >
                <PageTransition>
                  <ContactPage />
                </PageTransition>
              </PageWrapper>
            }
          />
          <Route
            path="/resources"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={false}
              >
                <PageTransition>
                  <ResourcesPage />
                </PageTransition>
              </PageWrapper>
            }
          />
          <Route
            path="/features"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={true}
              >
                <PageTransition>
                  <FeaturesPage />
                </PageTransition>
              </PageWrapper>
            }
          />

          {/* Template Marketplace */}
          <Route
            path="/templates"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={false}
              >
                <PageTransition>
                  <TemplateBrowse />
                </PageTransition>
              </PageWrapper>
            }
          />
          <Route
            path="/templates/:templateSlug"
            element={
              <PageWrapper
                showNavigation={true}
                showFooter={true}
                showCTA={false}
              >
                <PageTransition>
                  <TemplateDetail />
                </PageTransition>
              </PageWrapper>
            }
          />

          {/* Coming Soon Page */}
          <Route
            path="/coming-soon/:featureName"
            element={
              <PageTransition>
                <ComingSoonPage />
              </PageTransition>
            }
          />

          {/* Authentication Pages */}
          <Route
            path="/login"
            element={
              <PageTransition>
                <BusinessLogin />
              </PageTransition>
            }
          />
          <Route
            path="/signup"
            element={
              <PageTransition>
                <BusinessSignup />
              </PageTransition>
            }
          />
          <Route
            path="/account-created"
            element={
              <PageTransition>
                <AccountCreatedSuccess />
              </PageTransition>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PageTransition>
                <ForgotPassword />
              </PageTransition>
            }
          />
          <Route
            path="/unauthorized"
            element={
              <PageTransition>
                <UnauthorizedPage />
              </PageTransition>
            }
          />

          {/* Sign Out Route */}
          <Route path="/signout" element={<SignOut />} />

          {/* SuperAdmin Application */}
          <Route
            path="/admin/*"
            element={
              <AdminProtectedRoute>
                <PageWrapper
                  showNavigation={false}
                  showFooter={true}
                  showCTA={false}
                >
                  <SuperAdminApp />
                </PageWrapper>
              </AdminProtectedRoute>
            }
          />

          {/* Admin Application */}
          <Route
            path="/app/*"
            element={
              <PageWrapper
                showNavigation={false}
                showFooter={true}
                showCTA={false}
              >
                <AdminApp />
              </PageWrapper>
            }
          />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

function App() {
  return (
    <SupabaseAuthProvider>
      <Router>
        <AnimatedRoutes />
      </Router>
    </SupabaseAuthProvider>
  );
}

export default App;
